// Human-readable labels for the internal status / step / stage enums that
// leak into the UI. The raw values come from Prisma enums and the workflow
// engine; they read as "reconciliation_failed", "batch_ready", "key_entry",
// etc. Mapping them once here keeps every UI surface consistent and gives
// us a single place to tune wording.

import { batchRowLabel, preferredDisplayLabel } from "@capture/shared";

const BATCH_STATUS_LABELS: Record<string, string> = {
  processing: "Processing",
  batch_ready: "Ready for Export",
  needs_header_review: "Needs Header Review",
  balance_review: "Balance Review",
  reconciliation_failed: "Reconciliation Failed",
  reconciled: "Reconciled",
  exported: "Exported",
  archived: "Archived",
  error: "Error",
  rejected: "Rejected",
  on_hold: "On Hold",
};

const DOCUMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  needs_edit: "Needs Edit",
  needs_review: "Needs Review",
  on_hold: "On Hold",
  rejected: "Rejected",
  error: "Error",
  exported: "Exported",
  archived: "Archived",
};

const STEP_LABELS: Record<string, string> = {
  scan: "Scan",
  classify: "Classify",
  key: "Key Entry",
  key_entry: "Key Entry",
  verify: "Verify",
  verification: "Verify",
  qc: "QC",
  supervisor: "Supervisor",
  export: "Export",
  import: "Import",
  ocr: "OCR",
  "post-ocr": "Post-OCR",
  "pre-export": "Pre-Export",
};

function humanize(value: string): string {
  return value
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatBatchStatus(status: string | null | undefined): string {
  if (!status) return "—";
  return BATCH_STATUS_LABELS[status] ?? humanize(status);
}

export function formatDocumentStatus(status: string | null | undefined): string {
  if (!status) return "—";
  return DOCUMENT_STATUS_LABELS[status] ?? humanize(status);
}

export function formatStep(step: string | null | undefined): string {
  if (!step) return "—";
  return STEP_LABELS[step] ?? humanize(step);
}

/**
 * R-132 — render a batch's primary identifier.
 *
 * - If `displayId` is set, use it verbatim (e.g. `NOW_20260523_0001`).
 * - Otherwise fall back to `B-<first-6-chars-of-cuid>` so legacy rows still
 *   have *something* operator-readable. UI surfaces should style the fallback
 *   in a muted color to signal "no structured id assigned."
 */
export function formatBatchDisplayId(
  batch: { displayId?: string | null; id: string },
): string {
  if (batch.displayId && batch.displayId.trim()) return batch.displayId;
  return `B-${batch.id.slice(0, 6)}`;
}

/** R-132 — true when the batch has a structured displayId (vs. a cuid fallback). */
export function hasStructuredDisplayId(batch: {
  displayId?: string | null;
}): boolean {
  return !!batch.displayId && batch.displayId.trim().length > 0;
}

/**
 * R-132 — render a document's primary identifier.
 *
 * Falls back to `D-<first-6-chars-of-cuid>` for legacy rows.
 */
export function formatDocumentDisplayId(doc: {
  displayId?: string | null;
  id: string;
}): string {
  if (doc.displayId && doc.displayId.trim()) return doc.displayId;
  return `D-${doc.id.slice(0, 6)}`;
}

// Stage names and step types share the same vocabulary; alias for clarity at
// call sites that talk about "stages" (import/ocr/post-ocr/key-entry/…).
export const formatStage = formatStep;

/**
 * Format a retention-window duration (in minutes) as a human-readable
 * "X left" string. Used on both the RetentionQueuePage and the HistoryPage.
 *
 *   ≥ 1440 min  → "Xd left"   (whole days, rounded)
 *   ≥ 60 min    → "Xh left"   (whole hours, rounded)
 *   > 0 min     → "Xm left"
 *   ≤ 0 min     → "0m left"   (already expired / imminently expiring)
 */
export function formatMinutesRemaining(minutes: number): string {
  if (minutes >= 1440) {
    const days = Math.round(minutes / 1440);
    return `${days}d left`;
  }
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return `${hours}h left`;
  }
  if (minutes > 0) return `${minutes}m left`;
  return "0m left";
}

/**
 * R-167 WU-7 — render a batch's primary label using the frozen
 * `customDisplayLabel` column when set, falling back to the structured
 * `displayId`, then to `B-<cuid-prefix>` for legacy rows.
 *
 * Supersedes R-146's per-request `renderDisplayTemplate` path. Templates
 * are now resolved ONCE at create time by the WU-4 write-path service and
 * written into `customDisplayLabel`. Read surfaces just pick the frozen
 * value via `preferredDisplayLabel`.
 *
 * The `_template` parameter is intentionally unused — keeping the
 * positional argument lets us land the swap incrementally without breaking
 * dependents. Remove the parameter once every call site is updated.
 */
export function renderBatchLabel(
  batch: {
    customDisplayLabel?: string | null;
    displayId?: string | null;
    id: string;
  },
  _template?: string | null | undefined,
): string {
  // F-CAPTURE-009 — single-sourced so the API's Batch sort orders this exact text.
  return batchRowLabel(batch);
}

/**
 * WU-2.2 — canonical home for rendering an ISO timestamp as a locale string,
 * with an em-dash fallback for null. At least 5 older hand-rolled copies of
 * this exist across the app; retrofitting them onto this one is a follow-up,
 * not part of this change.
 */
export function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

/**
 * R-167 WU-7 — render a document's primary label. Same precedence as
 * {@link renderBatchLabel}: customDisplayLabel → displayId → cuid fallback.
 */
export function renderDocumentLabel(
  doc: {
    customDisplayLabel?: string | null;
    displayId?: string | null;
    id: string;
  },
  _template?: string | null | undefined,
): string {
  if (doc.customDisplayLabel && doc.customDisplayLabel.trim()) {
    return doc.customDisplayLabel;
  }
  if (doc.displayId && doc.displayId.trim()) {
    return preferredDisplayLabel({
      customDisplayLabel: doc.customDisplayLabel ?? null,
      displayId: doc.displayId,
    });
  }
  return formatDocumentDisplayId(doc);
}
